import { useEffect } from "react";
import type { SeoMetadata } from "@/seo/metadata";

interface SeoMetadataProps {
  metadata: SeoMetadata | null;
}

function setMeta(selector: string, attribute: "name" | "property", key: string, content?: string) {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (!content) {
    existing?.remove();
    return;
  }
  const element = existing || document.createElement("meta");
  element.setAttribute(attribute, key);
  element.content = content;
  element.dataset.axiomSeo = "true";
  if (!existing) document.head.appendChild(element);
}

function applyMetadata(metadata: SeoMetadata) {
  document.title = metadata.title;
  setMeta('meta[name="description"]', "name", "description", metadata.description);
  setMeta('meta[name="robots"]', "name", "robots", metadata.indexable
    ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    : "noindex, nofollow");

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = metadata.canonicalUrl;
  canonical.dataset.axiomSeo = "true";

  setMeta('meta[property="og:title"]', "property", "og:title", metadata.title);
  setMeta('meta[property="og:description"]', "property", "og:description", metadata.description);
  setMeta('meta[property="og:url"]', "property", "og:url", metadata.canonicalUrl);
  setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Axiom Manifold");
  setMeta('meta[property="og:type"]', "property", "og:type", metadata.type);
  setMeta('meta[property="og:locale"]', "property", "og:locale", "en_AU");
  setMeta('meta[property="og:image"]', "property", "og:image", metadata.imageUrl);
  setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", metadata.imageUrl ? metadata.title : undefined);

  setMeta('meta[name="twitter:card"]', "name", "twitter:card", metadata.imageUrl ? "summary_large_image" : "summary");
  setMeta('meta[name="twitter:title"]', "name", "twitter:title", metadata.title);
  setMeta('meta[name="twitter:description"]', "name", "twitter:description", metadata.description);
  setMeta('meta[name="twitter:image"]', "name", "twitter:image", metadata.imageUrl);
  setMeta('meta[name="twitter:image:alt"]', "name", "twitter:image:alt", metadata.imageUrl ? metadata.title : undefined);

  let jsonLd = document.head.querySelector<HTMLScriptElement>("#axiom-jsonld");
  if (metadata.jsonLd.length === 0) {
    jsonLd?.remove();
    return;
  }
  if (!jsonLd) {
    jsonLd = document.createElement("script");
    jsonLd.id = "axiom-jsonld";
    jsonLd.type = "application/ld+json";
    document.head.appendChild(jsonLd);
  }
  jsonLd.textContent = JSON.stringify(metadata.jsonLd.length === 1 ? metadata.jsonLd[0] : metadata.jsonLd);
}

export default function SeoMetadata({ metadata }: SeoMetadataProps) {
  useEffect(() => {
    if (metadata) applyMetadata(metadata);
  }, [metadata]);
  return null;
}
